import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import AuthService from '../../services/authService';
import { fetchAdminInvoices } from '../../services/admin/adminInvoiceApi';
import { fetchConferenceSubscriptionTracker } from '../../services/admin/subscriptionTrackerApi';
import { AuthContext } from '../../store/ContextStore';
import { CONFERENCE_TRACKER_SPORTS } from '../../types/admin/subscriptionTracker';
import { getSportLabel } from '../../utils/admin/formatSubscriptionDate';
import Spinner from '../Spinner';
import InvoiceUserSearch from './InvoiceUserSearch';
import InvoiceUserDetails from './InvoiceUserDetails';
import InvoiceSportTable from './InvoiceSportTable';
import InvoiceGeneratePanel from './InvoiceGeneratePanel';
import InvoiceListTable from './InvoiceListTable';
import {
  AdminInvoiceUserGroup,
  BillingPreview,
  GenerateInvoiceResponse,
  InvoiceSearchUser,
} from '../../types/adminInvoice';

const authService = new AuthService();

const CONFERENCE_SPORT_LABELS: Record<string, string> = {
  MFB: "Men's Football",
  MBB: "Men's Basketball",
  WBB: "Women's Basketball",
  MIH: "Men's Ice Hockey",
  WIH: "Women's Ice Hockey",
  MSO: "Men's Soccer",
  WSO: "Women's Soccer",
  MVB: "Men's Volleyball",
  WVB: "Women's Volleyball",
};

function conferenceSportLabel(code: string): string {
  return CONFERENCE_SPORT_LABELS[code] ?? getSportLabel(code);
}

const SPORT_OPTIONS = CONFERENCE_TRACKER_SPORTS.map((code) => ({
  value: code,
  label: `${conferenceSportLabel(code)} (${code})`,
}));

const INVOICE_USER_PAGE_SIZE = 10;

interface AdminInvoiceTabProps {
  /** Prefill Generated invoices list search (e.g. from Invoice Analytics chase row). */
  focusListSearch?: string | null;
  onFocusListSearchConsumed?: () => void;
}

const AdminInvoiceTab: React.FC<AdminInvoiceTabProps> = ({
  focusListSearch = null,
  onFocusListSearchConsumed,
}) => {
  const authContext = useContext(AuthContext);
  const openToast = authContext?.openToast as ((message: string, color: string) => void) | undefined;

  const [selectedUser, setSelectedUser] = useState<InvoiceSearchUser | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [billingPreview, setBillingPreview] = useState<BillingPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [selectedSportCodes, setSelectedSportCodes] = useState<string[]>([]);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generateSuccess, setGenerateSuccess] = useState<GenerateInvoiceResponse | null>(null);

  const [listSearch, setListSearch] = useState('');
  const [debouncedListSearch, setDebouncedListSearch] = useState('');
  const [invoiceGroups, setInvoiceGroups] = useState<AdminInvoiceUserGroup[]>([]);
  const [listHasMore, setListHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listLoadingMore, setListLoadingMore] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const listLoadingRef = useRef(false);
  const listOffsetRef = useRef(0);
  listLoadingRef.current = listLoading || listLoadingMore;

  const [filterSportCode, setFilterSportCode] = useState('');
  const [filterConferenceName, setFilterConferenceName] = useState('');
  const [conferenceOptions, setConferenceOptions] = useState<string[]>([]);
  const [conferencesLoading, setConferencesLoading] = useState(false);

  /** Idle browse mode: show recent invoices. Hide while searching or generating for a user. */
  const showRecentInvoices = !selectedUser && userSearchQuery.trim().length === 0;

  const [appliedListFilter, setAppliedListFilter] = useState<{
    sport_code?: string;
    conference_name?: string;
  }>({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedListSearch(listSearch);
      listOffsetRef.current = 0;
      setInvoiceGroups([]);
    }, 300);
    return () => clearTimeout(timer);
  }, [listSearch]);

  useEffect(() => {
    if (!focusListSearch?.trim()) return;
    setSelectedUser(null);
    setUserSearchQuery('');
    setListSearch(focusListSearch.trim());
    onFocusListSearchConsumed?.();
  }, [focusListSearch, onFocusListSearchConsumed]);

  const prevFilterSportRef = useRef(filterSportCode);
  useEffect(() => {
    const sportChanged = prevFilterSportRef.current !== filterSportCode;
    prevFilterSportRef.current = filterSportCode;
    if (sportChanged) {
      setFilterConferenceName('');
    }

    const controller = new AbortController();
    setConferencesLoading(true);

    const sportsToLoad = filterSportCode ? [filterSportCode] : [...CONFERENCE_TRACKER_SPORTS];

    (async () => {
      const names = new Set<string>();
      for (const code of sportsToLoad) {
        if (controller.signal.aborted) return;
        const result = await fetchConferenceSubscriptionTracker(
          { sport_code: code, limit: 200, offset: 0 },
          controller.signal
        );
        if (controller.signal.aborted || result.aborted) return;
        for (const c of result.data?.conferences ?? []) {
          if (c.conference_name) names.add(c.conference_name);
        }
      }
      setConferenceOptions([...names].sort((a, b) => a.localeCompare(b)));
      setConferencesLoading(false);
    })();

    return () => controller.abort();
  }, [filterSportCode]);

  const loadInvoiceList = useCallback(async (options?: {
    offset?: number;
    search?: string;
    sport_code?: string;
    conference_name?: string;
    append?: boolean;
  }) => {
    const append = options?.append ?? false;
    const nextOffset = options?.offset ?? (append ? listOffsetRef.current : 0);
    const nextSearch = options?.search ?? debouncedListSearch;
    const nextSport = options?.sport_code ?? appliedListFilter.sport_code;
    const nextConference = options?.conference_name ?? appliedListFilter.conference_name;

    if (append) {
      setListLoadingMore(true);
    } else {
      setListLoading(true);
      setListError(null);
    }

    const result = await fetchAdminInvoices({
      search: nextSearch || undefined,
      limit: INVOICE_USER_PAGE_SIZE,
      offset: nextOffset,
      sport_code: nextSport,
      conference_name: nextConference,
    });

    if (result.data) {
      setListError(null);
      setListHasMore(result.data.pagination.has_more);
      listOffsetRef.current = nextOffset + result.data.groups.length;
      setInvoiceGroups((prev) =>
        append ? [...prev, ...result.data!.groups] : result.data!.groups
      );
    } else if (!append) {
      setInvoiceGroups([]);
      setListHasMore(false);
      if (result.status === 404) {
        setListError(result.error ?? 'Unknown conference');
      } else if (result.status === 400) {
        setListError(result.error ?? 'Invalid filter');
      } else {
        setListError(result.error ?? 'Failed to load invoices');
      }
    }

    setListLoading(false);
    setListLoadingMore(false);
  }, [appliedListFilter, debouncedListSearch]);

  useEffect(() => {
    if (!showRecentInvoices) return;
    void loadInvoiceList({ offset: 0, append: false });
  }, [debouncedListSearch, appliedListFilter, showRecentInvoices]);

  const applyListFilters = () => {
    listOffsetRef.current = 0;
    setAppliedListFilter({
      sport_code: filterSportCode.trim() || undefined,
      conference_name: filterConferenceName.trim() || undefined,
    });
  };

  useEffect(() => {
    if (!showRecentInvoices || !listHasMore) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (listLoadingRef.current) return;
        void loadInvoiceList({ append: true });
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [showRecentInvoices, listHasMore, invoiceGroups.length, loadInvoiceList]);

  const fetchBillingPreview = useCallback(async (userId: number, signal?: AbortSignal) => {
    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const response = await authService.getBillingPreview(userId, signal);

      if (signal?.aborted) return;

      if (response.status === 200) {
        setBillingPreview(response.data as BillingPreview);
      } else if (response.status === 404) {
        setPreviewError('User not found');
        setBillingPreview(null);
      } else {
        setPreviewError(response.data?.detail || 'Failed to load billing preview');
        setBillingPreview(null);
      }
    } catch (err: unknown) {
      if (signal?.aborted) return;
      const error = err as { code?: string; name?: string };
      if (error.code === 'ERR_CANCELED' || error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      setPreviewError('Failed to load billing preview');
      setBillingPreview(null);
    } finally {
      if (!signal?.aborted) {
        setPreviewLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!selectedUser) {
      setBillingPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }

    const controller = new AbortController();
    setSelectedSportCodes([]);
    setGenerateError(null);
    setGenerateSuccess(null);
    fetchBillingPreview(selectedUser.id, controller.signal);

    return () => controller.abort();
  }, [selectedUser, fetchBillingPreview]);

  const handleClearUser = () => {
    setSelectedUser(null);
    setBillingPreview(null);
    setPreviewError(null);
    setSelectedSportCodes([]);
    setGenerateError(null);
    setGenerateSuccess(null);
  };

  const handleToggleSport = (sportCode: string) => {
    setSelectedSportCodes((prev) =>
      prev.includes(sportCode) ? prev.filter((c) => c !== sportCode) : [...prev, sportCode]
    );
    setGenerateError(null);
    setGenerateSuccess(null);
  };

  const handleGenerate = async () => {
    if (!selectedUser || selectedSportCodes.length === 0) return;

    setGenerateLoading(true);
    setGenerateError(null);
    setGenerateSuccess(null);

    try {
      const response = await authService.generateAdminInvoice({
        user_id: selectedUser.id,
        sport_codes: selectedSportCodes,
      });

      if (response.status === 200 || response.status === 201) {
        const data = response.data as GenerateInvoiceResponse;
        setGenerateSuccess(data);
        const email = data.email || selectedUser.email;
        openToast?.(`Invoice generated and sent to ${email}`, 'green');
        setSelectedSportCodes([]);
        await fetchBillingPreview(selectedUser.id);
        setListSearch('');
        setDebouncedListSearch('');
        listOffsetRef.current = 0;
        setInvoiceGroups([]);
        await loadInvoiceList({
          offset: 0,
          search: '',
          append: false,
          sport_code: appliedListFilter.sport_code,
          conference_name: appliedListFilter.conference_name,
        });
      } else if (response.status === 409) {
        setGenerateError('Cannot generate invoice — sport already has active subscription or grace period');
      } else if (response.status === 404) {
        setGenerateError('User not found');
      } else if (response.status === 400) {
        const detail = response.data?.detail;
        setGenerateError(typeof detail === 'string' ? detail : 'Invalid request');
      } else {
        setGenerateError(response.data?.detail || response.data?.message || 'Failed to generate invoice');
      }
    } catch {
      setGenerateError('Failed to generate invoice');
    } finally {
      setGenerateLoading(false);
    }
  };

  const clearConferenceFilters = () => {
    setFilterSportCode('');
    setFilterConferenceName('');
    setConferenceOptions([]);
    listOffsetRef.current = 0;
    setInvoiceGroups([]);
    setAppliedListFilter({});
  };

  const isInitialListLoad = listLoading && invoiceGroups.length === 0;

  return (
    <div className="p-4 bg-white shadow-md rounded-md mb-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Invoice</h2>
        <p className="text-gray-600 text-sm mt-1">
          Search a user to generate an invoice, or clear selection to browse generated invoices.
        </p>
      </div>

      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div className="min-w-0 flex-1 max-w-xl">
          <InvoiceUserSearch
            onSelect={setSelectedUser}
            onQueryChange={setUserSearchQuery}
          />
        </div>

        {showRecentInvoices && (
          <div className="flex flex-wrap items-end justify-end gap-2 shrink-0 xl:ml-4">
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              Sport
              <select
                className="w-40 max-w-full p-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filterSportCode}
                onChange={(e) => setFilterSportCode(e.target.value)}
              >
                <option value="">All sports</option>
                {SPORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              Conference
              <select
                className="w-48 max-w-full p-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
                value={filterConferenceName}
                disabled={conferencesLoading}
                onChange={(e) => setFilterConferenceName(e.target.value)}
              >
                <option value="">
                  {conferencesLoading ? 'Loading…' : 'All conferences'}
                </option>
                {conferenceOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-500">
              Filter list
              <input
                type="text"
                className="w-44 max-w-full p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="User, team, session…"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
              />
            </label>
            <button
              type="button"
              onClick={applyListFilters}
              disabled={listLoading}
              className="px-3 py-2 text-sm border border-transparent rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              Apply
            </button>
            {(filterSportCode ||
              filterConferenceName ||
              appliedListFilter.sport_code ||
              appliedListFilter.conference_name) && (
              <button
                type="button"
                onClick={clearConferenceFilters}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                listOffsetRef.current = 0;
                void loadInvoiceList({ offset: 0, append: false });
              }}
              disabled={listLoading}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Refresh
            </button>
          </div>
        )}
      </div>

      {selectedUser && (
        <section className="space-y-4">
          <InvoiceUserDetails
            user={billingPreview?.user ?? selectedUser}
            onClear={handleClearUser}
          />

          {previewLoading && (
            <div className="flex justify-center items-center h-32">
              <Spinner size="md" />
              <span className="ml-2 text-gray-600">Loading billing details...</span>
            </div>
          )}

          {previewError && !previewLoading && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {previewError}
            </div>
          )}

          {billingPreview && !previewLoading && (
            <>
              <h3 className="text-base font-medium text-gray-900">Sport Subscriptions</h3>
              <InvoiceSportTable
                sports={billingPreview.sports}
                selectedSportCodes={selectedSportCodes}
                onToggleSport={handleToggleSport}
                isSeatLocked={billingPreview.is_seat_locked === true}
                teamSeats={billingPreview.team_seats ?? []}
                seatLimit={billingPreview.seat_limit}
              />
              <InvoiceGeneratePanel
                selectedCount={selectedSportCodes.length}
                generateLoading={generateLoading}
                generateError={generateError}
                generateSuccess={generateSuccess}
                onGenerate={handleGenerate}
              />
            </>
          )}
        </section>
      )}

      {showRecentInvoices && (
        <section>
          <div className="mb-3">
            <h3 className="text-lg font-medium text-gray-900">Generated invoices</h3>
            <p className="text-sm text-gray-500">
              Grouped by billing user · Due or Paid per line · scroll for more
            </p>
          </div>

          {listError && (
            <div className="mb-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{listError}</div>
          )}

          {listLoading && invoiceGroups.length > 0 && (
            <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
              <Spinner size="sm" />
              <span>Updating list…</span>
            </div>
          )}

          {isInitialListLoad ? (
            <div className="flex justify-center items-center h-40">
              <Spinner size="md" />
              <span className="ml-2 text-gray-600">Loading invoices…</span>
            </div>
          ) : (
            <>
              <InvoiceListTable groups={invoiceGroups} />
              <div ref={loadMoreSentinelRef} className="h-4" aria-hidden />
              {listLoadingMore && (
                <div className="flex justify-center items-center py-4 text-sm text-gray-600">
                  <Spinner size="sm" />
                  <span className="ml-2">Loading more…</span>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
};

export default AdminInvoiceTab;
